/* 2026-07-24 · iOS native shell (Capacitor) network shim.
 *
 * Inside the shell the page origin is capacitor://localhost, so every
 * root-relative request ("/api/…", "/auth/refresh") that works on
 * wedge.repvantage.io would otherwise resolve against the capacitor origin
 * and fail. The wedge codebase has dozens of root-relative fetch call sites
 * (plus API_BASE = "/api" under the VITE_USE_PROXY prod build), so rather
 * than rewriting every call site, this module patches window.fetch and
 * navigator.sendBeacon ONCE at boot to prefix the production API origin.
 *
 * MUST be the first import in main.tsx so the patch lands before any other
 * module can capture the unpatched functions. On the web isNativeIOS() is
 * false and this module does nothing at all.
 *
 * Auth note: the shell authenticates through the wedge standalone token
 * bridge (Bearer tokens in localStorage · see packages/client/src/api.ts), not
 * cookies, so cross-origin fetches here need no credential changes. The
 * backend must allow the capacitor://localhost origin in FRONTEND_URLS.
 */
import { isNativeIOS } from './nativeBridge';

const WEDGE_API_ORIGIN = 'https://wedge.repvantage.io';

function rewrite(url: string): string {
  return url.startsWith('/') && !url.startsWith('//') ? WEDGE_API_ORIGIN + url : url;
}

/* Auth on device is Bearer-only. Every auth cookie the backend sets is
   sameSite='lax', so it is NEVER attached to a cross-site request from the
   capacitor://localhost origin. HttpClient and fetchWithAuth attach the
   Bearer themselves, but ~30 call sites use a RAW fetch with only
   credentials:'include' — including AuthContext.checkAuthStatus, which is the
   gate WedgeAuthGate uses to decide "signed in or not". On device those all
   401: /auth/me 401s, the refresh succeeds, and the retry 401s again because
   the retry is the same cookie-only fetch. The rep signs in and is bounced
   straight back to the landing page, and /wedge/billing/tier 401s so a paid
   rep reads as Free and a completed Apple purchase never unlocks.

   Rather than touch ~30 shared call sites (which are correct on the web,
   where the cookie IS same-site), attach the stored access token here, on the
   requests this shim already rewrites. Native-only and inert on web, same as
   the rest of this module. An explicit Authorization header always wins. */
const AT_KEY = 'rv_wedge_at';

function withBearer(init?: RequestInit, base?: Headers): RequestInit | undefined {
  let at: string | null = null;
  try { at = localStorage.getItem(AT_KEY); } catch { /* storage unavailable */ }
  if (!at) return init;
  /* Seed from the Request's own headers when re-pointing a Request object:
     fetch(request, init) replaces the header list wholesale with init.headers,
     so a Content-Type set on the Request was dropped on device. */
  const headers = new Headers(base || {});
  new Headers(init?.headers || {}).forEach((v, k) => headers.set(k, v));
  if (headers.has('Authorization')) return init;   // caller knows better
  headers.set('Authorization', `Bearer ${at}`);
  return { ...(init || {}), headers };
}

/** True only for OUR API origin. A string-prefix test matched
 *  https://wedge.repvantage.io.attacker.tld and https://wedge.repvantage.io@evil,
 *  which would have carried the rep's Bearer to a third party. */
function isOwnOrigin(url: string): boolean {
  try { return new URL(url).origin === WEDGE_API_ORIGIN; } catch { return false; }
}

if (typeof window !== 'undefined' && isNativeIOS()) {
  const realFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      if (typeof input === 'string') {
        const url = rewrite(input);
        /* Only add credentials to our own API, never to third parties. */
        return realFetch(url, isOwnOrigin(url) ? withBearer(init) : init);
      }
      if (input instanceof URL) return realFetch(input, init);
      if (input instanceof Request && input.url.startsWith(window.location.origin + '/')) {
        /* A Request built from a root-relative string has already been
           resolved against the capacitor origin · re-point it. 2026-07-27:
           honor an Authorization set on the REQUEST itself, not just on
           init — "an explicit Authorization header always wins" (above)
           was silently violated in this branch. */
        const path = input.url.slice(window.location.origin.length);
        const nextInit = input.headers.has('Authorization') ? init : withBearer(init, input.headers);
        return realFetch(new Request(WEDGE_API_ORIGIN + path, input), nextInit);
      }
    } catch { /* fall through to the untouched call */ }
    return realFetch(input as RequestInfo, init);
  }) as typeof window.fetch;

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const realBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = ((url: string | URL, data?: BodyInit | null) => {
      try {
        if (typeof url === 'string') return realBeacon(rewrite(url), data);
      } catch { /* fall through */ }
      return realBeacon(url, data);
    }) as typeof navigator.sendBeacon;
  }
}
